import React from 'react'
import { useInView } from 'react-intersection-observer'
import { Download, Briefcase, GraduationCap, Award, Code } from 'lucide-react'
import Button from './Button'
import Card from './Card'
import { featuredProjects, otherProjects } from '../data/projects'
import { education as educationData, certificates } from '../data/education'

const Resume = () => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const currentYear = new Date().getFullYear()
  const experienceStartYear = 2024
  const yearsExperience = Math.max(1, currentYear - experienceStartYear)
  const projectsCount = (featuredProjects?.length || 0) + (otherProjects?.length || 0)
  const certCount = certificates?.length || 0
  const stats = [
    { icon: Briefcase, label: 'Years Experience', value: String(yearsExperience) },
    { icon: Code, label: 'Projects Completed', value: String(projectsCount) },
    { icon: Award, label: 'Certifications', value: String(certCount) },
    { icon: GraduationCap, label: 'Satisfied Clients', value: String(projectsCount) },
  ]
  const primaryEdu = educationData?.[0]

  return (
    <section
      id="resume"
      ref={ref}
      className="py-20 px-4 sm:px-6 lg:px-8 bg-navy-pale dark:bg-navy-deep"
    >
      <div className="max-w-7xl mx-auto">
        <div
          className={`transition-all duration-1000 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-navy-dark dark:text-navy-lightest mb-4">
              Resume
            </h2>
            <div className="w-20 h-1 bg-gradient-to-r from-navy-medium to-navy-deep dark:from-github-border dark:to-github-border mx-auto mb-4"></div>
            <p className="text-xl text-navy-base dark:text-navy-soft max-w-2xl mx-auto mb-8">
              Download my full resume or view the highlights below
            </p>
            <Button
              href="/resume.pdf"
              variant="primary"
              size="lg"
              className="inline-flex"
              download
            >
              <Download size={20} className="mr-2" />
              Download Resume
            </Button>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {stats.map((stat, idx) => (
              <Card
                key={stat.label}
                className={`p-6 text-center transition-all duration-700 delay-${
                  idx * 100
                } ${
                  inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-navy-medium/10 rounded-full mb-4">
                  <stat.icon className="text-navy-medium" size={32} />
                </div>
                <div className="text-3xl font-bold text-navy-dark dark:text-navy-lightest mb-2">
                  {stat.value}
                </div>
                <div className="text-navy-base dark:text-navy-soft">{stat.label}</div>
              </Card>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h3 className="text-2xl font-bold text-navy-dark dark:text-navy-lightest mb-6 flex items-center gap-2">
                <Briefcase className="text-navy-medium" size={28} />
                Work Experience
              </h3>
              <div className="space-y-6">
                <Card className="p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="text-xl font-semibold text-navy-dark dark:text-navy-lightest">
                      Founder & Lead Full-Stack Developer
                    </h4>
                    <span className="text-sm text-navy-base/70 dark:text-navy-soft/70">
                      {experienceStartYear} - Present
                    </span>
                  </div>
                  <p className="text-navy-medium font-medium mb-3">SoftGen.lk</p>
                  <ul className="list-disc list-inside text-navy-base dark:text-navy-soft space-y-2">
                    <li>Built and launched a full-service software solutions platform</li>
                    <li>Developed a modern, fully responsive web platform using HTML, CSS, Bootstrap, JavaScript, and PHP</li>
                    <li>Implemented SEO-focused content architecture and performance optimizations</li>
                    <li>Achieved 40% increase in business inquiries within the first 3 months; 95+ Lighthouse performance and SEO scores</li>
                  </ul>
                </Card>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold text-navy-dark dark:text-navy-lightest mb-6 flex items-center gap-2">
                <GraduationCap className="text-navy-medium" size={28} />
                Education
              </h3>
              <div className="space-y-6">
                {primaryEdu && (
                  <Card className="p-6">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-xl font-semibold text-navy-dark dark:text-navy-lightest">
                        {primaryEdu.degree}
                      </h4>
                      <span className="text-sm text-navy-base/70 dark:text-navy-soft/70">
                        {primaryEdu.period || primaryEdu.duration}
                      </span>
                    </div>
                    <p className="text-navy-medium font-medium mb-3">{primaryEdu.institution}</p>
                    <p className="text-navy-base dark:text-navy-soft">{primaryEdu.description}</p>
                  </Card>
                )}

                <Card className="p-6">
                  <h4 className="text-xl font-semibold text-navy-dark dark:text-navy-lightest mb-4">
                    Certifications
                  </h4>
                  {(() => {
                    const certs = certificates || []
                    const edu = educationData || []

                    const findEdu = (re) => edu.find((e) => re.test(`${e.degree || ''} ${e.subtitle || ''}`))
                    const titleOf = (e) => `${e.degree}${e.subtitle ? ' – ' + e.subtitle : ''}`

                    const picks = []
                    const l3 = certs.find((c) => /level\s*3.*diploma.*it/i.test(c?.title || ''))
                    if (l3) picks.push({ id: `cert-${l3.id}`, title: l3.title })
                    const dipEng = edu.find((e) => /diploma.*english/i.test(e?.degree || ''))
                    if (dipEng) picks.push({ id: `edu-${dipEng.id}`, title: titleOf(dipEng) })
                    const al = findEdu(/advanced\s*level|\bA\/?L\b|g\.c\.e.*advanced/i)
                    if (al) picks.push({ id: `edu-${al.id}-al`, title: titleOf(al) })
                    const ol = findEdu(/ordinary\s*level|\bO\/?L\b|g\.c\.e.*ordinary/i)
                    if (ol) picks.push({ id: `edu-${ol.id}-ol`, title: titleOf(ol) })

                    const seen = new Set()
                    const curated = []
                    picks.forEach((p) => {
                      if (p && !seen.has(p.title)) {
                        seen.add(p.title)
                        curated.push(p)
                      }
                    })
                    if (curated.length < 4) {
                      certs.forEach((c) => {
                        if (curated.length >= 4) return
                        const t = c?.title
                        if (t && !seen.has(t)) {
                          seen.add(t)
                          curated.push({ id: `cert-${c.id}`, title: t })
                        }
                      })
                    }

                    return (
                      <ul className="space-y-3 text-navy-base dark:text-navy-soft">
                        {curated.slice(0, 4).map((item) => (
                          <li key={item.id} className="flex items-start gap-2">
                            <Award className="text-navy-medium mt-1 flex-shrink-0" size={18} />
                            <span>{item.title}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  })()}
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Resume
